"use client";
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AlertTriangle, Check, ChevronsUpDown, Sparkles } from "lucide-react";
import * as React from "react";
import { cn } from "../lib/utils.js";
import { formatMappingValue, getModelMappings, getModelName, getProviderName, isDeactivated, isUnstableStability, parseMappingValue, } from "./model-entries.js";
import { getProviderIcon } from "./provider-icons.js";
import { Button } from "./ui/button.js";
import { Command, CommandGroup, CommandInput, CommandItem, CommandList, } from "./ui/command.js";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover.js";
function normalize(value) {
    return value.toLowerCase().replace(/[-_\s.]+/g, "");
}
function EntryIcon(_a) {
    var entry = _a.entry, className = _a.className;
    if (!entry.mapping) {
        return _jsx(Sparkles, { className: cn("text-primary", className) });
    }
    var Icon = getProviderIcon(entry.mapping.providerId);
    return _jsx(Icon, { className: className });
}
export function ModelMappingSelector(_a) {
    var models = _a.models, providers = _a.providers, value = _a.value, onValueChange = _a.onValueChange, _b = _a.placeholder, placeholder = _b === void 0 ? "Select a mapping…" : _b, _c = _a.includeCanonicalModels, includeCanonicalModels = _c === void 0 ? false : _c, _d = _a.includeMappings, includeMappings = _d === void 0 ? true : _d, _e = _a.includeRegions, includeRegions = _e === void 0 ? false : _e, _f = _a.includeDeactivated, includeDeactivated = _f === void 0 ? false : _f, filter = _a.filter, _g = _a.maxResults, maxResults = _g === void 0 ? 100 : _g, className = _a.className, contentClassName = _a.contentClassName, _h = _a.align, align = _h === void 0 ? "start" : _h, disabled = _a.disabled;
    var _j = React.useState(false), open = _j[0], setOpen = _j[1];
    var _k = React.useState(""), search = _k[0], setSearch = _k[1];
    var deferredSearch = React.useDeferredValue(search);
    var entries = React.useMemo(function () {
        var _a;
        var now = new Date();
        var out = [];
        var providerById = new Map(providers.map(function (p) { return [p.id, p]; }));
        for (var _i = 0, _b = __spreadArray([], models, true).sort(function (a, b) { return a.id.localeCompare(b.id); }); _i < _b.length; _i++) {
            var model = _b[_i];
            var modelName = getModelName(model);
            if (includeCanonicalModels && (!filter || filter({ model: model }))) {
                out.push({
                    value: model.id,
                    model: model,
                    searchText: normalize("".concat(modelName, " ").concat(model.id, " auto")),
                });
            }
            if (!includeMappings) {
                continue;
            }
            for (var _c = 0, _d = getModelMappings(model); _c < _d.length; _c++) {
                var mapping = _d[_c];
                if (!includeDeactivated && isDeactivated(mapping.deactivatedAt, now)) {
                    continue;
                }
                if (filter && !filter({ model: model, mapping: mapping })) {
                    continue;
                }
                var provider = providerById.get(mapping.providerId);
                out.push({
                    value: formatMappingValue(mapping.providerId, model.id, includeRegions ? mapping.region : null),
                    model: model,
                    mapping: mapping,
                    provider: provider,
                    searchText: normalize("".concat(modelName, " ").concat(model.id, " ").concat(mapping.providerId, " ").concat(getProviderName(provider), " ").concat((_a = mapping.region) !== null && _a !== void 0 ? _a : "")),
                });
            }
        }
        if (includeRegions) {
            return out;
        }
        var seen = new Set();
        return out.filter(function (entry) {
            if (seen.has(entry.value)) {
                return false;
            }
            seen.add(entry.value);
            return true;
        });
    }, [
        models,
        providers,
        includeCanonicalModels,
        includeMappings,
        includeRegions,
        includeDeactivated,
        filter,
    ]);
    var filteredEntries = React.useMemo(function () {
        var tokens = deferredSearch
            .toLowerCase()
            .split(/[-_\s.]+/)
            .filter(Boolean);
        if (tokens.length === 0) {
            return entries;
        }
        return entries.filter(function (entry) {
            return tokens.every(function (token) { return entry.searchText.includes(token); });
        });
    }, [entries, deferredSearch]);
    var visibleEntries = filteredEntries.slice(0, maxResults);
    var hiddenCount = filteredEntries.length - visibleEntries.length;
    var selected = React.useMemo(function () {
        if (!value) {
            return null;
        }
        var exact = entries.find(function (entry) { return entry.value === value; });
        if (exact) {
            return exact;
        }
        var parsed = parseMappingValue(value);
        var model = models.find(function (m) { return m.id === parsed.modelId; });
        if (!model) {
            return null;
        }
        var mapping = parsed.providerId
            ? getModelMappings(model).find(function (m) { return m.providerId === parsed.providerId; })
            : undefined;
        return {
            value: value,
            model: model,
            mapping: mapping,
            provider: providers.find(function (p) { return p.id === parsed.providerId; }),
            searchText: "",
        };
    }, [entries, models, providers, value]);
    return (_jsxs(Popover, { open: open, onOpenChange: setOpen, children: [_jsx(PopoverTrigger, { asChild: true, children: _jsxs(Button, { variant: "outline", role: "combobox", "aria-expanded": open, disabled: disabled, className: cn("w-[320px] justify-between", className), children: [selected ? (_jsxs("span", { className: "flex min-w-0 items-center gap-2", children: [_jsx(EntryIcon, { entry: selected, className: "h-4 w-4 shrink-0" }), _jsx("span", { className: "truncate font-medium", children: getModelName(selected.model) }), _jsx("span", { className: "truncate text-xs text-muted-foreground", children: selected.mapping
                                        ? getProviderName(selected.provider) ||
                                            selected.mapping.providerId
                                        : "All providers" })] })) : (_jsx("span", { className: "truncate text-muted-foreground", children: placeholder })), _jsx(ChevronsUpDown, { className: "ml-2 h-4 w-4 shrink-0 opacity-50" })] }) }), _jsx(PopoverContent, { className: cn("w-[380px] p-0", contentClassName), align: align, children: _jsxs(Command, { shouldFilter: false, children: [_jsx(CommandInput, { placeholder: includeMappings ? "Search model or provider…" : "Search model…", value: search, onValueChange: setSearch }), _jsxs(CommandList, { children: [visibleEntries.length === 0 ? (_jsxs("div", { className: "py-6 text-center text-sm text-muted-foreground", children: ["No ", includeMappings ? "mappings" : "models", " found."] })) : null, _jsx(CommandGroup, { children: visibleEntries.map(function (entry) {
                                        var _a;
                                        var unstable = isUnstableStability((_a = entry.mapping) === null || _a === void 0 ? void 0 : _a.stability) ||
                                            isUnstableStability(entry.model.stability);
                                        return (_jsxs(CommandItem, { value: entry.value, onSelect: function () {
                                                onValueChange(entry.value);
                                                setOpen(false);
                                            }, className: "gap-2", children: [_jsx(Check, { className: cn("h-4 w-4 shrink-0", value === entry.value ? "opacity-100" : "opacity-0") }), _jsx(EntryIcon, { entry: entry, className: "h-4 w-4 shrink-0" }), _jsxs("span", { className: "flex min-w-0 flex-col", children: [_jsxs("span", { className: "flex items-center gap-1", children: [_jsx("span", { className: "truncate font-medium", children: getModelName(entry.model) }), unstable ? (_jsx(AlertTriangle, { className: "h-3.5 w-3.5 shrink-0 text-yellow-600 dark:text-yellow-500" })) : null] }), _jsx("span", { className: "truncate font-mono text-[11px] text-muted-foreground", children: entry.value })] }), entry.mapping ? (_jsx("span", { className: "ml-auto shrink-0 pl-2 text-xs text-muted-foreground", children: getProviderName(entry.provider) ||
                                                        entry.mapping.providerId })) : (_jsxs("span", { className: "ml-auto shrink-0 pl-2 text-xs text-muted-foreground", children: [getModelMappings(entry.model).length, "p"] }))] }, entry.value));
                                    }) }), hiddenCount > 0 ? (_jsxs("div", { className: "border-t px-3 py-2 text-xs text-muted-foreground", children: [hiddenCount, " more \u2014 refine your search."] })) : null] })] }) })] }));
}
//# sourceMappingURL=model-mapping-selector.js.map